// Home.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { API_BASE_URL, ItemList, renderModal, closeModal, renderConfirmationModal, fetchData, handleDuplicate, LoadingOverlay } from './Common';

import './Home.css';

const projectsUrl = `${API_BASE_URL}/projects`;

function Home({ setSelectedProject }) {

  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [isProjectsLoaded, setIsProjectsLoaded] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const [currentPage, setCurrentPage] = useState(1); // Track current page number
  const itemsPerPage = 10; // Maximum items per page

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalMessageColor, setModalMessageColor] = useState('');
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  useEffect(() => {
    fetchData(projectsUrl, setProjects, setIsProjectsLoaded, 'projects');
  }, []);

  const handleShareProject = (projectId) => {
    setSelectedProjectId(projectId);
    setIsShareModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsShareModalOpen(false);
    setSelectedProjectId(null);
  };

  const ShareProjectModal = ({ projectId, isOpen, onClose }) => {
  const [sharedUsers, setSharedUsers] = useState([]);
  const [newUser, setNewUser] = useState('');
  const [token] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (isOpen) {
      fetchSharedUsers();
    }
  }, [isOpen]);

  const fetchSharedUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/shared-users/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSharedUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching shared users:', error);
    }
  };

  const handleAddUser = async () => {
    if (!newUser) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/share-project/${projectId}`,
        { username: newUser, permission: 'read-only' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSharedUsers([...sharedUsers, { username: newUser, permission: 'read-only' }]);
      setNewUser('');
      console.log(response.data.message || 'User added successfully');
      fetchSharedUsers();
      fetchData(projectsUrl, setProjects, setIsProjectsLoaded, 'projects');
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };

  const handleRemoveUser = async (username) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/unshare-project/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { username },
      });
      setSharedUsers(sharedUsers.filter((user) => user.username !== username));
      console.log(response.data.message || 'User removed successfully');
      fetchSharedUsers();
      fetchData(projectsUrl, setProjects, setIsProjectsLoaded, 'projects');
    } catch (error) {
      console.error('Error removing user:', error);
    }
  };

  const handlePermissionToggle = async (username) => {
    const currentUser = sharedUsers.find(user => user.username === username);
    const newPermission = currentUser.permission === 'read-only' ? 'edit' : 'read-only';

    try {
      const response = await axios.put(
        `${API_BASE_URL}/update-permission/${projectId}`,
        { username, permission: newPermission },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSharedUsers(sharedUsers.map(user =>
        user.username === username ? { ...user, permission: newPermission } : user
      ));
      console.log(response.data.message || 'Permission updated successfully');
    } catch (error) {
      console.error('Error updating permission:', error);
    }
  };

  // Styles with modern enhancements
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    transition: 'opacity 0.3s ease',
  };

  const modalStyle = {
    backgroundColor: '#fff',
    borderRadius: '10px',
    padding: '30px',
    width: '600px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    animation: 'fadeIn 0.3s ease-in-out',
  };

  const headerStyle = {
    margin: '0',
    paddingBottom: '20px',
    borderBottom: '2px solid #eee',
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
  };

  const sharedUsersStyle = {
    marginBottom: '20px',
  };

  const listStyle = {
    listStyleType: 'none',
    padding: 0,
    margin: 0,
    maxHeight: '200px',
    overflowY: 'auto',
  };

  const listItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    padding: '10px',
    borderRadius: '6px',
    backgroundColor: '#f9f9f9',
    boxShadow: '0 1px 5px rgba(0, 0, 0, 0.1)',
  };

  const buttonBaseStyle = {
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const removeButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#ff4d4f',
    color: '#fff',
    border: 'none',
  };

  const toggleStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    marginRight: '10px',
  };

  const addUserStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  };

  const inputStyle = {
    flex: 1,
    padding: '10px',
    marginRight: '10px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '16px',
  };

  const addButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
  };

  const closeButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#6c757d',
    color: '#fff',
    marginTop: '20px',
  };

  return isOpen ? (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <h2 style={headerStyle}>Partilhar Projeto</h2>
        <div style={sharedUsersStyle}>
          <h4 style={{ marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>Partilhado com:</h4>
          {sharedUsers.length > 0 ? (
            <ul style={listStyle}>
              {sharedUsers.map((user) => (
                <li key={user.username} style={listItemStyle}>
                  <span>{user.username}</span>
                  <div>
                    <button
                      style={toggleStyle}
                      onClick={() => handlePermissionToggle(user.username)}
                    >
                      {user.permission === 'read-only' ? 'Leitura' : 'Edição'}
                    </button>
                    <button style={removeButtonStyle} onClick={() => handleRemoveUser(user.username)}>
                      Remover
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>Sem utilizadores partilhados.</p>
          )}
        </div>
        <div style={addUserStyle}>
          <input
            type="text"
            placeholder="Insira username ou email"
            value={newUser}
            onChange={(e) => setNewUser(e.target.value)}
            style={inputStyle}
          />
          <button style={addButtonStyle} onClick={handleAddUser}>
            Adicionar
          </button>
        </div>
        <button style={closeButtonStyle} onClick={onClose}>
          Fechar
        </button>
      </div>
    </div>
  ) : null;
};



  const handleDeleteProject = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/projects/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}` // Include JWT token in Authorization header
        }
      });
      // Update the projects list after deletion
      setProjects(projects.filter(project => project.database_id !== projectId));

      setModalMessage('Projeto eliminado!');
      setModalMessageColor('#E5004E');
      setIsModalOpen(true);

      const newNumProjects = projects.length - 1;
    const newNumPages = Math.ceil(newNumProjects / itemsPerPage);

    // If the current page is greater than the new number of pages after deletion,
    // set the current page to the last page
    if (currentPage > newNumPages) {
      setCurrentPage(newNumPages);
    }

    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleDuplicateProject = (projectId) => {
    handleDuplicate(
      'project',
      projectId,
      setProjects,
      projects,
      'Projeto Duplicado!',
      setIsDuplicating,
      setModalMessage,
      setModalMessageColor,
      setIsModalOpen
    );
  };

const handleEdit = (projectId) => {
    const project = projects.find(p => p.database_id === projectId);
    localStorage.setItem('selectedProject', JSON.stringify(project));
    setSelectedProject(project);
    navigate(`/project/${projectId}/dados`);
};


const confirmDeleteProject = async () => {
        const token = localStorage.getItem('token');
        await handleDeleteProject(projectToDelete, token, setProjects, projects, setModalMessage, setModalMessageColor, setIsModalOpen);
        setIsConfirmationModalOpen(false);
    };

const handleDeleteClick = (projectId) => {
        setProjectToDelete(projectId);
        setIsConfirmationModalOpen(true);
    };

  return (
  <div className="home-container">
    <h2>Projetos</h2>
    <p>Acesso a todos os projetos criados</p>


<ItemList
      tipo="project"
      isLoaded={isProjectsLoaded}
      onEdit={handleEdit}
      onDelete={handleDeleteClick}
      onDuplicate={handleDuplicateProject}
      onShare={handleShareProject}

        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        DataArray={projects}
        itemsPerPage={itemsPerPage}
      />

<ShareProjectModal
        projectId={selectedProjectId}
        isOpen={isShareModalOpen}
        onClose={handleCloseModal}
/>

{renderConfirmationModal(isConfirmationModalOpen, "Tem a certeza que pretende eliminar o projeto?", confirmDeleteProject, () => setIsConfirmationModalOpen(false))}

{renderModal(isModalOpen, modalMessage, closeModal(setIsModalOpen), modalMessageColor)}

<LoadingOverlay isDuplicating={isDuplicating} />

  </div>
);

}

export default Home;

